import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt, faDownload, faEnvelope, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogFooter
} from "../ui/dialog";
import Button from "../button-v1";
import styles from "../../styles/billing.module.css";
import { parseDateSafe } from "../../utils/dateFormatters";

const AppealLetterModal = ({
  isOpen,
  onClose,
  onGenerateAppeal,
  onDownloadAppeal,
  programType,
  patientName,
  lastGeneratedDate,
  isDownloadEnabled,
  patientId,
  getPatientDocumentsById,
}) => {
  const [appealDocs, setAppealDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  useEffect(() => {
    if (isOpen && patientId && getPatientDocumentsById) {
      fetchAppealDocuments();
    }
    if (!isOpen) {
      setAppealDocs([]);
    }
  }, [isOpen, patientId]);

  const fetchAppealDocuments = async () => {
    setLoadingDocs(true);
    try {
      const data = await getPatientDocumentsById(patientId, "appeal");
      setAppealDocs(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error("Error fetching appeal documents:", error);
      setAppealDocs([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleDocumentDownload = (doc) => {
    if (!doc.document_url) return;
    const link = document.createElement("a");
    link.href = doc.document_url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = parseDateSafe(dateStr);
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className={styles.alertTitle}>Appeal Letter</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className={styles.modalBody}>
            <div className={styles.downloadSection}>
              <h4>Generate {programType} Appeal Letter</h4>
              <p>
                Generate or Download Appeal Letter for <b>{patientName}</b>
              </p>

              <div className={styles.auditReportContainer}>
                <div className={styles.auditReportHeader}>
                  <div className={styles.auditReportIcon}>
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      size="3x"
                      color="#0f1e59"
                    />
                  </div>
                  <div className={styles.auditReportInfo}>
                    <h5>Appeal Letter</h5>
                    <p>
                      Generate an appeal letter for denied claims and send via email.
                    </p>
                  </div>
                </div>

                {/* Last Generated Info */}
                {lastGeneratedDate && (
                  <div style={{
                    backgroundColor: "#f0f9ff",
                    border: "1px solid #bae6fd",
                    borderRadius: "6px",
                    padding: "12px 16px",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px"
                  }}>
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      size="lg"
                      color="#0369a1"
                    />
                    <div>
                      <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#0c4a6e" }}>
                        Appeal Letter Generated
                      </p>
                      <p style={{ margin: 0, fontSize: "12px", color: "#0369a1" }}>
                        Last Generated on: {lastGeneratedDate}
                      </p>
                    </div>
                  </div>
                )}

                <div className={styles.auditReportActions}>
                  <Button
                    variant="default"
                    className={`${styles.downloadPdfButton} ${!isDownloadEnabled ? styles.disabledBtn : ""}`}
                    onClick={onDownloadAppeal}
                    disabled={!isDownloadEnabled}
                  >
                    <FontAwesomeIcon
                      icon={faDownload}
                      className={styles.buttonIcon}
                    />
                    Download Appeal Letter
                  </Button>

                  <Button
                    variant="default"
                    className={styles.sharePdfButton}
                    onClick={onGenerateAppeal}
                  >
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className={styles.buttonIcon}
                    />
                    Generate Appeal Letter
                  </Button>
                </div>

                {/* Previous Appeal Letters */}
                <hr className={styles.appealDocsDivider} />
                <div className={styles.appealDocsSection}>
                  <h5>Previous Appeal Letters</h5>

                  {loadingDocs ? (
                    <div className={styles.appealDocsLoading}>
                      <div className={styles.appealDocsSpinner} />
                      Loading documents...
                    </div>
                  ) : appealDocs.length === 0 ? (
                    <div className={styles.appealDocsEmpty}>
                      No previous appeal letters found.
                    </div>
                  ) : (
                    <div className={styles.appealDocsList}>
                      {appealDocs.map((doc) => (
                        <div key={doc.id} className={styles.appealDocRow}>
                          <div className={styles.appealDocInfo}>
                            <FontAwesomeIcon
                              icon={faFilePdf}
                              className={styles.appealDocIcon}
                            />
                            <div className={styles.appealDocDetails}>
                              <div className={styles.appealDocName}>
                                {doc.document_name || "Appeal Letter"}
                              </div>
                              <div className={styles.appealDocDate}>
                                {formatDate(doc.uploaded_at || doc.created_at)}
                              </div>
                            </div>
                          </div>
                          <button
                            className={styles.appealDocDownloadBtn}
                            onClick={() => handleDocumentDownload(doc)}
                            title="Download"
                          >
                            <FontAwesomeIcon icon={faDownload} size="sm" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AppealLetterModal;
